import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import avatecLogo from '../../assets/logos/avatec-white.png';
import './SlideOfficesGallery.css';

const officeItems = [
  {
    id: 'office-netherlands',
    src: '/images/offices-gallery/Netherlands/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%201m32s%5D.png',
    alt: 'Avatec Office - Netherlands',
    country: 'NETHERLANDS',
    city: 'Rotterdam',
  },
  {
    id: 'office-india',
    src: '/images/offices-gallery/India/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%202m01s%5D.png',
    alt: 'Avatec Office - India',
    country: 'INDIA',
    city: 'Mumbai',
  },
  {
    id: 'office-sweden',
    src: '/images/offices-gallery/Sweeden/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%202m11s%5D.png',
    alt: 'Avatec Office - Sweden',
    country: 'SWEDEN',
    city: 'Gothenburg',
  },
  {
    id: 'office-uae-fujairah',
    src: '/images/offices-gallery/UAE/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%202m25s%5D.png',
    alt: 'Avatec Office - UAE Fujairah',
    country: 'UAE',
    city: 'Dubai',
  },
  {
    id: 'office-uae-dubai',
    src: '/images/offices-gallery/UAE/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%202m28s%5D.png',
    alt: 'Avatec Office - UAE Dubai',
    country: 'UAE',
    city: 'Dubai',
  },
  {
    id: 'office-morocco-1',
    src: '/images/offices-gallery/Morocco/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%202m44s%5D.png',
    alt: 'Avatec Office - Morocco Agadir 1',
    country: 'MOROCCO',
    city: 'Agadir',
  },
  {
    id: 'office-morocco-2',
    src: '/images/offices-gallery/Morocco/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%202m51s%5D.png',
    alt: 'Avatec Office - Morocco Agadir 2',
    country: 'MOROCCO',
    city: 'Agadir',
  },
  {
    id: 'office-estonia',
    src: '/images/offices-gallery/Estonia/Avatec%20-%20Avatec%20Marine%20%5B79g3kz8XbYI%20-%201335x751%20-%203m07s%5D.png',
    alt: 'Avatec Office - Estonia',
    country: 'ESTONIA',
    city: 'Tallinn',
  },
];

function SlideOfficesGallery() {
  const prefersReduced = useReducedMotion();
  const reduceMotion = !!prefersReduced;
  const [isVisible, setIsVisible] = useState(false);
  const [activeItem, setActiveItem] = useState<null | (typeof officeItems)[number]>(null);
  const ease: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (!activeItem) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveItem(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeItem]);

  const fadeUp = (delay = 0) =>
    reduceMotion
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : {
          initial: { opacity: 0, y: 18 },
          animate: isVisible ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.7, delay, ease },
        };

  return (
    <div className="slide slide-offices-gallery">
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />

      <div className="og-bg">
        <div className="og-grid-bg" />
        <div className="og-glow og-glow-left" />
        <div className="og-glow og-glow-right" />
        <div className="og-noise" />
      </div>

      <div className="og-content">
        <motion.div className="og-header" {...fadeUp(0.05)}>
          <span className="og-eyebrow">AVATEC MARINE</span>
          <h1 className="og-title">OFFICES GALLERY</h1>
        </motion.div>

        <div className="og-grid">
          {officeItems.map((item, index) => (
            <motion.button
              key={item.id}
              className={`og-item og-item--${index + 1}`}
              type="button"
              onClick={() => setActiveItem(item)}
              aria-label={`Open office image ${item.country} ${item.city}`}
              {...fadeUp(0.12 + index * 0.05)}
            >
              <img src={item.src} alt={item.alt} loading="lazy" />
              <div className="og-label">
                <span className="og-label-country">{item.country}</span>
                <span className="og-label-city">{item.city}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeItem && (
          <motion.div
            className="og-modal"
            role="dialog"
            aria-modal="true"
            aria-label={activeItem.alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.25 }}
            onClick={() => setActiveItem(null)}
          >
            <motion.div
              className="og-modal-card"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: reduceMotion ? 0 : 0.25 }}
              onClick={(event) => event.stopPropagation()}
            >
              <img src={activeItem.src} alt={activeItem.alt} />
              <div className="og-modal-label">
                <span className="og-modal-country">{activeItem.country}</span>
                <span className="og-modal-city">{activeItem.city}</span>
              </div>
              <button
                type="button"
                className="og-modal-close"
                onClick={() => setActiveItem(null)}
                aria-label="Close image"
              >
                ×
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SlideOfficesGallery;
